import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Drawer, Spin, Tag, Avatar, Descriptions, Empty, Divider,
  Image, Tabs, Timeline, Card, Badge, Button, Rate,
  Input, InputNumber, Tooltip, message, Modal, Form, DatePicker, Select, Upload,
} from "antd";
import {
  UserOutlined, PhoneOutlined, CarOutlined,
  CheckCircleOutlined, ClockCircleOutlined, ArrowRightOutlined,
  DollarOutlined, ShoppingOutlined, TruckOutlined, HistoryOutlined,
  SendOutlined, PlusOutlined, EditOutlined, StopOutlined, SwapOutlined,
  RollbackOutlined, NotificationOutlined,
} from "@ant-design/icons";
import { movableBackStatuses } from "./MoveStatusModal";
import { SendToSuppliersModal } from "./SendToSuppliersModal";
import { RecordPaymentModal } from "./RecordPaymentModal";
import { UnavailablePartsModal } from "./UnavailablePartsModal";
import { BillingDetailsModal } from "./BillingDetailsModal";
import { PriceUpdateDrawer } from "./PriceUpdateDrawer";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import axios from "axios";
import { API_DOMAIN } from "../constants";
import { AuthContext } from "../contexts/AuthContext";
import { useSocket } from "../contexts/SocketContext";
import { URGENCY_CFG } from "./pipeline";
import { getAgentColor, agentInitials, _fetchSRMUsers, loadConditionLabels, conditionLabel, fetchMiscOptions, loadSideLabels, sideLabel, mapChatItem, engineLayoutLabel, transmissionTypeLabel, drivetrainLabel, useQv4Theme } from "./helpers";
import { CHAT_V2_API, CHAT_MESSAGES_API } from "./constants";
import { CustomerProfileButton } from "./CustomerProfileButton";
import { PartsEditor, EMPTY_PART } from "./PartsEditor";
import { QueryImagePicker } from "./QueryImagePicker";

// Resolves the customer's WATI chat by phone (same search step
// openChatByPhone/QuerysetV4.js uses), then fetches its most recent
// messages and returns just the image ones — a self-contained equivalent of
// ChatPanelMessages' onImageMessagesChange, needed here because the Parts
// tab's "Add photos" flow isn't opened from within an already-loaded chat,
// so there's no live chatImages state to piggyback on.
async function fetchCustomerChatImages(phone, userId) {
  const normalized = (phone || "").replace(/\D/g, "");
  if (!normalized) return [];
  const headers = { accessToken: localStorage.getItem("accessToken") };
  try {
    const searchRes = await axios.post(
      CHAT_V2_API,
      { userId, continuationToken: "", filterType: 0, subConditions: null, searchString: normalized, searchOptionType: 0 },
      { headers }
    );
    if (!searchRes.data.success) return [];
    const hit = (searchRes.data.data?.final_data || [])
      .map(mapChatItem)
      .find(c => (c.phone || "").replace(/\D/g, "") === normalized);
    if (!hit) return [];

    const msgRes = await axios.post(
      CHAT_MESSAGES_API,
      { chatId: hit.conversationId, lastMessageId: null, userId },
      { headers }
    );
    if (!msgRes.data.success) return [];
    const raw = Array.isArray(msgRes.data.data) ? msgRes.data.data : (msgRes.data.data?.data || []);
    return raw.filter(m => m.eventType === "message" && (m.type || "").toLowerCase() === "image");
  } catch {
    return [];
  }
}

dayjs.extend(utc);

const fmt = (val) => val != null ? val : "—";
const fmtDate = (val) => val ? dayjs(val).format("DD MMM YYYY, hh:mm A") : "—";
const fmtAED = (val) => val != null ? `AED ${Number(val).toLocaleString()}` : "—";

const fmtElapsed = (ms) => {
  if (!ms || ms < 0) return null;
  const totalMins = Math.floor(ms / 60000);
  if (totalMins < 1) return "< 1m";
  if (totalMins < 60) return `${totalMins}m`;
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hrs < 24) return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs > 0 ? `${days}d ${remHrs}h` : `${days}d`;
};

const ElapsedDivider = ({ ms }) => {
  const { t } = useQv4Theme();
  const label = fmtElapsed(ms);
  if (!label) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "8px 0 2px", color: t.textSub }}>
      <div style={{ flex: 1, height: 1, background: t.border }} />
      <span style={{ fontSize: 10, whiteSpace: "nowrap" }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: t.border }} />
    </div>
  );
};

// ── Timeline ──────────────────────────────────────────────────────────────────
const EVENT_CFG = {
  supplier_quote_received: { color: "green",  label: "Quote received" },
  sent_to_suppliers:       { color: "blue",   label: "Sent to suppliers" },
  draft_created:           { color: "gray",   label: "Draft created" },
  status_changed:          { color: "blue",   label: "Status changed" },
  unavailable_parts_notified: { color: "orange", label: "Notified: Unavailable Parts" },
  part_created:            { color: "blue",   label: "Part added" },
  // Not yet emitted by the backend — added ahead of time so the Add-Photos
  // flow's timeline entries render with a proper label the moment the
  // backend spec (docs/FUTURE_TASKS.md, append-images endpoint) ships,
  // instead of falling back to the generic humanized label until noticed.
  images_added:            { color: "blue",   label: "Photos added" },
};

function TaskTimeline({ timeline, parts, userMap, taskStatus }) {
  const { t } = useQv4Theme();
  if (!timeline?.length) return null;

  const partNameById = {};
  const supplierNameById = {};
  parts.forEach(p => {
    if (p.id && p.part_name) partNameById[p.id] = p.part_name;
    (p.supplier_quotes ?? p.quotes ?? []).forEach(q => {
      if (q.supplier_id && q.supplier_name) supplierNameById[q.supplier_id] = q.supplier_name;
    });
  });

  const sorted = [...timeline].sort((a, b) => new Date(b.time) - new Date(a.time));

  const TERMINAL = new Set(["cancelled", "completed", "delivered", "disabled"]);
  const isCompleted = taskStatus && TERMINAL.has(taskStatus);
  const oldestMs  = new Date(sorted[sorted.length - 1].time).getTime();
  const newestMs  = new Date(sorted[0].time).getTime();
  const endMs     = isCompleted ? newestMs : Date.now();
  const totalMs   = endMs - oldestMs;
  const totalStr  = fmtElapsed(totalMs);

  const renderContent = (e, elapsedMs) => {
    const cfg = EVENT_CFG[e.event];
    const userName = e.user_id ? (userMap[e.user_id] ?? `User #${e.user_id}`) : null;
    const metaEl = (
      <div style={{ fontSize: 10, color: t.textSub, marginTop: 3, display: "flex", gap: 6 }}>
        {userName && <span style={{ color: t.textSub, fontWeight: 500 }}>{userName}</span>}
        {userName && <span>·</span>}
        <span>{fmtDate(e.time)}</span>
      </div>
    );

    let body;
    if (e.event === "supplier_quote_received") {
      const partName = partNameById[e.query_part_id] || `Part #${e.query_part_id}`;
      const supplierName = supplierNameById[e.supplier_id] || `Supplier #${e.supplier_id}`;
      body = (
        <>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{cfg?.label ?? "Quote received"}</div>
          <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>
            <span style={{ fontWeight: 500 }}>{supplierName}</span>{" · "}{partName}
          </div>
          <div style={{ fontSize: 11, color: "#389e0d", fontWeight: 600, marginTop: 1 }}>
            AED {Number(e.quoted_price || 0).toLocaleString()}
            {Number(e.delivery_cost) > 0 ? ` + AED ${e.delivery_cost} delivery` : ""}
            {e.warranty_days ? ` · ${e.warranty_days}d warranty` : ""}
          </div>
        </>
      );
    } else {
      const label = cfg?.label ?? e.event.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      body = (
        <>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
          {e.details && <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>{e.details}</div>}
        </>
      );
    }

    return (
      <div>
        {body}
        {metaEl}
        <ElapsedDivider ms={elapsedMs} />
      </div>
    );
  };

  return (
    <div>
      {totalStr && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          background: t.hover, borderRadius: 6,
          padding: "5px 10px", marginBottom: 10, fontSize: 11,
        }}>
          <ClockCircleOutlined style={{ color: t.textSub, fontSize: 12 }} />
          <span style={{ color: t.textSub }}>
            <strong style={{ color: t.text }}>{totalStr}</strong>
            {" total · "}
            {isCompleted ? `draft → ${taskStatus.replace(/_/g, " ")}` : "draft → now (ongoing)"}
          </span>
        </div>
      )}
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 10, color: t.textSub }}>
        Timeline ({timeline.length})
      </div>
      <Timeline
        items={sorted.map((e, i) => {
          const nextOlder = sorted[i + 1];
          const elapsedMs = nextOlder
            ? new Date(e.time).getTime() - new Date(nextOlder.time).getTime()
            : null;
          return {
            key: i,
            color: EVENT_CFG[e.event]?.color ?? "gray",
            children: renderContent(e, elapsedMs),
          };
        })}
      />
    </div>
  );
}

// ── Overview Tab ─────────────────────────────────────────────────────────────
export function OverviewTab({ task, afterSales, parts, userMap }) {
  const { t } = useQv4Theme();
  const assignee = task.assigned_user;
  const assigneeName = assignee
    ? (assignee.name ? `${assignee.name}${assignee.surname ? " " + assignee.surname : ""}` : assignee.username)
    : null;
  const images = Array.isArray(task.images) ? task.images : [];
  const timeline = Array.isArray(task.timeline) ? task.timeline : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Assignee */}
      {assigneeName && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar style={{ backgroundColor: getAgentColor(assigneeName), fontSize: 13 }}>
            {agentInitials(assigneeName)}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{assigneeName}</div>
            <div style={{ fontSize: 11, color: t.textSub }}>{assignee.user_type || "Staff"}</div>
          </div>
        </div>
      )}

      {/* Customer + Vehicle */}
      <Descriptions column={1} size="small" bordered
        labelStyle={{ width: 130, fontWeight: 600, fontSize: 12 }}
        contentStyle={{ fontSize: 12 }}>
        {task.customer?.customer_name && (
          <Descriptions.Item label="Customer">
            <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, width: "100%" }}>
              {task.customer.customer_name}
              {task.customer?.customer_mobile && <CustomerProfileButton phone={task.customer.customer_mobile} />}
            </span>
          </Descriptions.Item>
        )}
        {task.customer?.customer_real_name && task.customer.customer_real_name !== task.customer.customer_name && (
          <Descriptions.Item label="Real Name">{task.customer.customer_real_name}</Descriptions.Item>
        )}
        {task.customer?.customer_mobile && (
          <Descriptions.Item label="Mobile">
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <PhoneOutlined style={{ fontSize: 11 }} />
              {task.customer.customer_mobile}
            </span>
          </Descriptions.Item>
        )}
        {task.vehicle && (
          <Descriptions.Item label="Vehicle">
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <CarOutlined style={{ fontSize: 11 }} />
              {[task.vehicle.brand_name || (typeof task.vehicle.brand === "string" ? task.vehicle.brand : null),
                task.vehicle.model_name || (typeof task.vehicle.model === "string" ? task.vehicle.model : null),
                task.vehicle.variant,
                task.vehicle.year].filter(Boolean).join(" · ")}
            </span>
          </Descriptions.Item>
        )}
        {task.vehicle?.vin && (
          <Descriptions.Item label="VIN">{task.vehicle.vin}</Descriptions.Item>
        )}
        {task.vehicle?.transmission_type && (
          <Descriptions.Item label="Transmission">{transmissionTypeLabel(task.vehicle.transmission_type)}</Descriptions.Item>
        )}
        {task.vehicle?.transmission_code && (
          <Descriptions.Item label="Transmission Code">{task.vehicle.transmission_code}</Descriptions.Item>
        )}
        {task.vehicle?.engine_code && (
          <Descriptions.Item label="Engine Code">{task.vehicle.engine_code}</Descriptions.Item>
        )}
        {task.vehicle?.engine_displacement && (
          <Descriptions.Item label="Engine Displacement">{task.vehicle.engine_displacement}</Descriptions.Item>
        )}
        {task.vehicle?.engine_layout && (
          <Descriptions.Item label="Engine Layout">{engineLayoutLabel(task.vehicle.engine_layout)}</Descriptions.Item>
        )}
        {task.vehicle?.drivetrain && (
          <Descriptions.Item label="Drivetrain">{drivetrainLabel(task.vehicle.drivetrain)}</Descriptions.Item>
        )}
        {task.vehicle?.fuel_type && (
          <Descriptions.Item label="Fuel Type">{task.vehicle.fuel_type}</Descriptions.Item>
        )}
        {task.vehicle?.country_spec && (
          <Descriptions.Item label="Country Spec">{task.vehicle.country_spec}</Descriptions.Item>
        )}
        {task.vehicle?.trim_info && (
          <Descriptions.Item label="Trim Info">{task.vehicle.trim_info}</Descriptions.Item>
        )}
        {task.vehicle?.notes && (
          <Descriptions.Item label="Vehicle Notes">{task.vehicle.notes}</Descriptions.Item>
        )}
        {task.created_at && (
          <Descriptions.Item label="Created">{fmtDate(task.created_at)}</Descriptions.Item>
        )}
      </Descriptions>

      {/* Images */}
      {images.length > 0 && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: t.textSub }}>
            Images ({images.length})
          </div>
          <Image.PreviewGroup>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
              {images.map((src, i) => {
                const url = src.startsWith("http") ? src : `${API_DOMAIN}${src}`;
                return (
                  <Image key={i} src={url} width={80} height={80}
                    style={{ objectFit: "cover", borderRadius: 6, flexShrink: 0, cursor: "pointer", border: `1px solid ${t.border}` }}
                    preview={{ src: url }}
                  />
                );
              })}
            </div>
          </Image.PreviewGroup>
        </div>
      )}

      {/* Timeline */}
      <TaskTimeline timeline={timeline} parts={parts} userMap={userMap} taskStatus={task.status} />

      {/* Remarks */}
      {task.remarks && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, color: t.textSub }}>Remarks</div>
          <div style={{ fontSize: 12, color: t.textSub, whiteSpace: "pre-wrap", background: t.headerBg, padding: "8px 10px", borderRadius: 6, border: `1px solid ${t.border}` }}>
            {task.remarks}
          </div>
        </div>
      )}

      {/* After-Sales summary */}
      {afterSales && (
        <div>
          <Divider style={{ margin: "4px 0 12px" }} />
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <CheckCircleOutlined style={{ color: "#52c41a" }} /> Sale Confirmed
          </div>
          <Descriptions column={2} size="small" bordered
            labelStyle={{ fontWeight: 600, fontSize: 12 }}
            contentStyle={{ fontSize: 12 }}>
            {afterSales.so_number && (
              <Descriptions.Item label="SO #" span={afterSales.po_number ? 1 : 2}>
                {afterSales.so_url
                  ? <a href={afterSales.so_url} target="_blank" rel="noreferrer">{afterSales.so_number}</a>
                  : afterSales.so_number}
              </Descriptions.Item>
            )}
            {afterSales.po_number && (
              <Descriptions.Item label="PO #">
                {afterSales.po_url
                  ? <a href={afterSales.po_url} target="_blank" rel="noreferrer">{afterSales.po_number}</a>
                  : afterSales.po_number}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Sale">{fmtAED(afterSales.sale_amount)}</Descriptions.Item>
            <Descriptions.Item label="Paid">{fmtAED(afterSales.paid_amount)}</Descriptions.Item>
            <Descriptions.Item label="Balance">{fmtAED(afterSales.balance_amount)}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={afterSales.payment_status === "paid" ? "green" : afterSales.payment_status === "partial" ? "orange" : "red"} style={{ margin: 0 }}>
                {afterSales.payment_status_label || afterSales.payment_status}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}
    </div>
  );
}

// ── Parts Tab ─────────────────────────────────────────────────────────────────
function PartsTab({
  parts, images, queryPk, userId, customerPhone, onPartPatched, onManagePrices,
  sideOptions, conditionOptions, partsInfoOptions, partsInfoLoading, onAddNewPartInfo,
}) {
  const { t, isDark } = useQv4Theme();
  const [cardMode, setCardMode] = useState({});   // { [partId]: 'editing' | 'disabling' }
  const [editFields, setEditFields] = useState({});
  const [disableReason, setDisableReason] = useState({});
  const [saving, setSaving] = useState({});
  // Same growable-list + staged-photos shape as Create Query's form (shared
  // PartsEditor/QueryImagePicker components) instead of the old single-part
  // free-text fields — lets a new part requirement and its reference photos
  // be added to an already-live query later in its lifecycle, matching how
  // the query was first created.
  const [addMode, setAddMode] = useState(false);
  const [newParts, setNewParts] = useState([{ ...EMPTY_PART }]);
  const [newImages, setNewImages] = useState([]);
  // Lazy-loaded once, the first time "Add Part" is opened — this drawer
  // isn't opened from within an already-loaded chat (unlike Create Query,
  // which is), so there's no live chat image list to reuse; fetched fresh
  // here instead. Cached for the rest of this drawer's lifetime.
  const [chatImages, setChatImages] = useState([]);
  const [chatImagesLoaded, setChatImagesLoaded] = useState(false);
  const [chatImagesLoading, setChatImagesLoading] = useState(false);
  useEffect(() => {
    if (!addMode || chatImagesLoaded || !customerPhone) return;
    setChatImagesLoaded(true);
    setChatImagesLoading(true);
    fetchCustomerChatImages(customerPhone, userId).then(setChatImages).finally(() => setChatImagesLoading(false));
  }, [addMode, chatImagesLoaded, customerPhone, userId]);
  const [adding, setAdding] = useState(false);

  const headers = { accessToken: localStorage.getItem("accessToken") };

  const startEdit = (p) => {
    setCardMode(m => ({ ...m, [p.id]: "editing" }));
    // condition_preference is a numeric code, and conditionOptions' values
    // come straight from get_misc_mode — but the part's own stored value
    // (from /query_workflow/task/get) isn't guaranteed to come back as the
    // same JS type (e.g. "1" vs 1), so the Select's strict value===option
    // match can silently show nothing even though the data itself is fine.
    // conditionLabel()/sideLabel() already dodge this by comparing as
    // strings — matching here the same way and using the option's own
    // (correctly-typed) value keeps the controlled Select in sync.
    const matchedCondition = conditionOptions.find(o => String(o.value) === String(p.condition_preference));
    setEditFields(f => ({ ...f, [p.id]: {
      userId,
      side: p.side || "",
      part_number: p.part_number || "",
      condition_preference: matchedCondition ? matchedCondition.value : (p.condition_preference || ""),
      quantity: p.quantity || 1,
    }}));
  };

  const endMode = (partId) => setCardMode(m => ({ ...m, [partId]: null }));

  const setField = (partId, key, val) =>
    setEditFields(f => ({ ...f, [partId]: { ...f[partId], [key]: val } }));

  const handleSave = async (partId) => {
    setSaving(s => ({ ...s, [partId]: true }));
    try {
      await axios.patch(`${API_DOMAIN}/query-parts/${partId}`, editFields[partId], { headers });
      onPartPatched?.();
      endMode(partId);
    } catch {
      message.error("Failed to save changes");
    } finally {
      setSaving(s => ({ ...s, [partId]: false }));
    }
  };

  const handleDisable = async (partId) => {
    const reason = (disableReason[partId] || "").trim();
    if (!reason) { message.warning("Please enter a reason"); return; }
    setSaving(s => ({ ...s, [partId]: true }));
    try {
      await axios.patch(`${API_DOMAIN}/query-parts/${partId}`, { userId, status: "disabled", remark: reason }, { headers });
      onPartPatched?.();
      endMode(partId);
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to disable part");
    } finally {
      setSaving(s => ({ ...s, [partId]: false }));
    }
  };

  const handleEnable = async (partId) => {
    setSaving(s => ({ ...s, [partId]: true }));
    try {
      await axios.patch(`${API_DOMAIN}/query-parts/${partId}`, { userId, status: "active" }, { headers });
      onPartPatched?.();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to re-enable part");
    } finally {
      setSaving(s => ({ ...s, [partId]: false }));
    }
  };

  const resetAddForm = () => {
    setAddMode(false);
    setNewParts([{ ...EMPTY_PART }]);
    setNewImages([]);
  };

  const handleAddNewPartInfoLocal = async (value, idx) => {
    try {
      await onAddNewPartInfo?.(value);
      setNewParts(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], part_name: value };
        return next;
      });
    } catch {
      message.error("Failed to save new part name");
    }
  };

  // Uploads any staged photos (same per-image status pattern as Create
  // Query), creates each new part via the existing /query-parts endpoint,
  // then appends the uploaded photos to the live query via the new
  // append-images endpoint spec'd in docs/FUTURE_TASKS.md (uzd_srm_backend)
  // — not yet implemented server-side, so this call will 404/fail until
  // that ships, but nothing else here needs to change once it does.
  const handleAddParts = async () => {
    const validParts = newParts.filter(p => p.part_name?.trim());
    if (validParts.length === 0) { message.warning("Add at least one part"); return; }
    setAdding(true);
    try {
      let uploadedImages = [];
      if (newImages.length > 0) {
        setNewImages(imgs => imgs.map(img => ({ ...img, uploadStatus: "uploading" })));
        const results = await Promise.all(newImages.map(async img => {
          try {
            const fd = new FormData();
            if (img.source === "upload") {
              fd.append("file", img.file);
            } else {
              const blobRes = await axios.post(
                `${API_DOMAIN}/wati_messages/download_wati_media_file`,
                { file_name: img.data },
                { headers, responseType: "blob" }
              );
              const blob = blobRes.data;
              const ext = (blob.type || "image/jpeg").split("/")[1] || "jpg";
              fd.append("file", new File([blob], `chat_image_${Date.now()}.${ext}`, { type: blob.type }));
            }
            const res = await axios.post(`${API_DOMAIN}/whatsapp/image_upload`, fd, { headers });
            setNewImages(imgs => imgs.map(x => x.id === img.id ? { ...x, uploadStatus: "done" } : x));
            return { id: img.id, data: res.data, ok: true };
          } catch {
            setNewImages(imgs => imgs.map(x => x.id === img.id ? { ...x, uploadStatus: "error" } : x));
            return { id: img.id, ok: false };
          }
        }));
        if (results.some(r => !r.ok)) {
          message.error("Failed to upload one or more images. Please try again.");
          setAdding(false);
          return;
        }
        uploadedImages = results.map(r => r.data);
      }

      await Promise.all(validParts.map(p => axios.post(`${API_DOMAIN}/query-parts`, {
        part_name: p.part_name.trim(),
        quantity: p.quantity || 1,
        ...(p.side && { side: p.side }),
        ...(p.condition_preference != null && { condition_preference: p.condition_preference }),
        ...(p.part_number?.trim() && { part_number: p.part_number.trim() }),
        userId, query_pk: queryPk,
      }, { headers })));

      if (uploadedImages.length > 0) {
        await axios.post(`${API_DOMAIN}/queryset_v2/${queryPk}/images`, { userId, images: uploadedImages }, { headers });
      }

      onPartPatched?.();
      resetAddForm();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to add part(s)");
    } finally {
      setAdding(false);
    }
  };

  const renderPartCard = (p) => {
    const isSelected = (q) => q.id === p.selected_quote_id;
    const quotes = p.quotes || [];
    const hasQuotes = quotes.length > 0;
    const isDisabled = p.status === "disabled";
    const mode = cardMode[p.id] ?? null;
    const fields = editFields[p.id] ?? {};
    const isSaving = saving[p.id] ?? false;

    return (
      <Card key={p.id} size="small"
        style={{
          borderRadius: 8,
          opacity: isDisabled ? 0.75 : 1,
          border: isDisabled ? "1px solid #ffa39e" : undefined,
          background: isDisabled ? (isDark ? "rgba(255,77,79,0.12)" : "#fff2f0") : undefined,
        }}
        styles={{ body: { padding: "10px 14px" } }}
      >
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{
            fontWeight: 700, fontSize: 13,
            textDecoration: isDisabled ? "line-through" : "none",
            color: isDisabled ? "#cf1322" : undefined,
          }}>
            {p.part_name || `Part #${p.id}`}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {isDisabled
              ? <Tag color="red" style={{ margin: 0, fontSize: 10 }}>Disabled</Tag>
              : <Tag style={{ margin: 0, fontSize: 10 }}>{p.status_label || p.status}</Tag>
            }
            {!isDisabled && (
              <Button size="small" type="text"
                icon={<EditOutlined style={{ fontSize: 11 }} />}
                style={{ color: mode === "editing" ? "#1677ff" : t.textSub }}
                onClick={() => mode === "editing" ? endMode(p.id) : startEdit(p)}
              />
            )}
            {!isDisabled && !hasQuotes && mode !== "disabling" && (
              <Tooltip title="Disable this part" getPopupContainer={n => n.parentElement ?? document.body}>
                <Button size="small" type="text" danger
                  icon={<StopOutlined style={{ fontSize: 11 }} />}
                  onClick={() => setCardMode(m => ({ ...m, [p.id]: "disabling" }))}
                />
              </Tooltip>
            )}
            {!isDisabled && hasQuotes && mode !== "disabling" && (
              <Tooltip title="Can't disable — suppliers have already quoted on this part" getPopupContainer={n => n.parentElement ?? document.body}>
                <Button size="small" type="text" disabled icon={<StopOutlined style={{ fontSize: 11 }} />} />
              </Tooltip>
            )}
            {isDisabled && (
              <Button size="small" loading={isSaving}
                style={{ fontSize: 10, height: 22, padding: "0 8px" }}
                onClick={() => handleEnable(p.id)}
              >
                Re-enable
              </Button>
            )}
          </div>
        </div>

        {/* Disabled reason */}
        {isDisabled && p.remark && (
          <div style={{ fontSize: 11, color: "#cf1322", marginBottom: 4 }}>
            Reason: {p.remark}
          </div>
        )}

        {/* Edit mode */}
        {mode === "editing" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Side</div>
                <Select size="small" style={{ width: "100%" }} allowClear
                  value={fields.side || null}
                  onChange={v => setField(p.id, "side", v ?? "")}
                  options={sideOptions}
                  placeholder="Side"
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Condition</div>
                <Select size="small" style={{ width: "100%" }} allowClear
                  value={fields.condition_preference || null}
                  onChange={v => setField(p.id, "condition_preference", v ?? "")}
                  options={conditionOptions}
                  placeholder="Condition"
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Part Number</div>
                <Input size="small" value={fields.part_number}
                  onChange={e => setField(p.id, "part_number", e.target.value)}
                  placeholder="OEM or aftermarket #"
                />
              </div>
              <div style={{ width: 84 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Qty</div>
                <InputNumber size="small" min={1} value={fields.quantity}
                  onChange={val => setField(p.id, "quantity", val)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <Button size="small" onClick={() => endMode(p.id)}>Cancel</Button>
              <Button size="small" type="primary" loading={isSaving} onClick={() => handleSave(p.id)}>Save</Button>
            </div>
          </div>
        )}

        {/* Disable confirmation mode */}
        {mode === "disabling" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: t.textSub, marginBottom: 4 }}>Reason for disabling</div>
            <Input.TextArea rows={2} size="small" autoFocus
              placeholder="Why is this part being removed?"
              value={disableReason[p.id] || ""}
              onChange={e => setDisableReason(r => ({ ...r, [p.id]: e.target.value }))}
              style={{ marginBottom: 6 }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <Button size="small" onClick={() => endMode(p.id)}>Cancel</Button>
              <Button size="small" danger type="primary" loading={isSaving} onClick={() => handleDisable(p.id)}>
                Confirm Disable
              </Button>
            </div>
          </div>
        )}

        {/* Normal metadata (view mode only) */}
        {mode === null && (
          <div style={{ fontSize: 11, color: t.textSub, marginBottom: hasQuotes && !isDisabled ? 8 : 0 }}>
            Qty: {p.quantity}
            {p.side ? ` · ${sideLabel(p.side)}` : ""}
            {p.condition_preference ? ` · ${conditionLabel(p.condition_preference)}` : ""}
            {p.part_number ? ` · Part# ${p.part_number}` : ""}
            {p.selling_price != null ? ` · Selling: ${fmtAED(p.selling_price)}` : ""}
          </div>
        )}

        {/* Quotes (only for active parts) */}
        {!isDisabled && hasQuotes && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: t.textSub, marginBottom: 2 }}>
              Supplier Quotes ({quotes.length})
            </div>
            {quotes.map(q => (
              <div key={q.id} style={{
                padding: "6px 10px", borderRadius: 6, fontSize: 11,
                border: isSelected(q) ? "1.5px solid #52c41a" : `1px solid ${t.border}`,
                background: isSelected(q) ? (isDark ? "rgba(82,196,26,0.12)" : "#f6ffed") : t.headerBg,
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {q.supplier_name || `Supplier #${q.supplier_id}`}
                  </span>
                  <span style={{ color: t.textSub }}>
                    {q.quoted_price != null ? fmtAED(q.quoted_price) : "—"}
                    {q.delivery_cost != null ? ` · Delivery: ${fmtAED(q.delivery_cost)}` : ""}
                    {q.warranty_days ? ` · ${q.warranty_days}d warranty` : ""}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                  {isSelected(q) && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>Selected</Tag>}
                  {q.availability_status && <Tag style={{ margin: 0, fontSize: 10 }}>{q.availability_status}</Tag>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  };

  const activeParts = parts.filter(p => p.status !== "disabled");
  const disabledParts = parts.filter(p => p.status === "disabled");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {onManagePrices && (
        <Button size="small" icon={<DollarOutlined />} onClick={onManagePrices} style={{ alignSelf: "flex-start" }}>
          Manage Prices
        </Button>
      )}
      {images?.length > 0 && (
        <Image.PreviewGroup>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
            {images.map((src, i) => {
              const url = src.startsWith("http") ? src : `${API_DOMAIN}${src}`;
              return (
                <Image key={i} src={url} width={72} height={72}
                  style={{ objectFit: "cover", borderRadius: 6, flexShrink: 0, cursor: "pointer", border: `1px solid ${t.border}` }}
                  preview={{ src: url }}
                />
              );
            })}
          </div>
        </Image.PreviewGroup>
      )}
      {activeParts.length === 0 && !addMode && (
        <Empty description="No parts added" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "24px 0 8px" }} />
      )}
      {activeParts.map(renderPartCard)}
      {disabledParts.length > 0 && (
        <>
          <Divider style={{ margin: "4px 0", fontSize: 11, color: t.textSub }}>
            Disabled ({disabledParts.length})
          </Divider>
          {disabledParts.map(renderPartCard)}
        </>
      )}

      {/* Add Part(s) + photos form — same shape as the Create Query form */}
      {addMode ? (
        <Card size="small" style={{ borderRadius: 8, border: "1.5px dashed #1677ff" }}
          styles={{ body: { padding: "10px 14px", display: "flex", flexDirection: "column", gap: 10 } }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: "#1677ff" }}>Add to this query</div>
          <PartsEditor
            parts={newParts}
            setParts={updater => setNewParts(prev => typeof updater === "function" ? updater(prev) : updater)}
            sideOptions={sideOptions}
            conditionOptions={conditionOptions}
            partsInfoOptions={partsInfoOptions}
            partsInfoLoading={partsInfoLoading}
            onAddNewPartInfo={handleAddNewPartInfoLocal}
            label="New parts"
          />
          <QueryImagePicker
            images={newImages}
            setImages={updater => setNewImages(prev => typeof updater === "function" ? updater(prev) : updater)}
            imageMessages={chatImages}
            imageMessagesLoading={chatImagesLoading}
            label="New photos"
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
            <Button size="small" disabled={adding} onClick={resetAddForm}>Cancel</Button>
            <Button size="small" type="primary" loading={adding} onClick={handleAddParts}>Save</Button>
          </div>
        </Card>
      ) : (
        <Button type="dashed" size="small" block icon={<PlusOutlined />}
          style={{ color: "#1677ff", borderColor: "#1677ff" }}
          onClick={() => setAddMode(true)}
        >
          Add Part
        </Button>
      )}
    </div>
  );
}

// ── Fulfillment Tab ───────────────────────────────────────────────────────────
function FulfillmentTab({ fulfillments, parts }) {
  const { t } = useQv4Theme();
  if (!fulfillments?.length) return <Empty description="No fulfillment data yet" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "40px 0" }} />;

  const partName = (id) => parts.find(p => p.id === id)?.part_name || `Part #${id}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {fulfillments.map(f => (
        <Card key={f.id} size="small" style={{ borderRadius: 8 }} styles={{ body: { padding: "10px 14px" } }}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>
            {partName(f.query_part_id)}
          </div>
          <Descriptions column={2} size="small"
            labelStyle={{ fontSize: 11, fontWeight: 600, color: t.textSub }}
            contentStyle={{ fontSize: 11 }}>
            {f.purchase_status && (
              <Descriptions.Item label="Purchase">{f.purchase_status_label || f.purchase_status}</Descriptions.Item>
            )}
            {f.delivery_status && (
              <Descriptions.Item label="Delivery">{f.delivery_status_label || f.delivery_status}</Descriptions.Item>
            )}
            {f.item_location_status && (
              <Descriptions.Item label="Location" span={2}>{f.item_location_status_label || f.item_location_status}</Descriptions.Item>
            )}
            {f.delivery_mode && (
              <Descriptions.Item label="Mode">{f.delivery_mode_label || f.delivery_mode}</Descriptions.Item>
            )}
            {f.purchase_cost != null && (
              <Descriptions.Item label="Cost">{fmtAED(f.purchase_cost)}</Descriptions.Item>
            )}
            {f.delivery_contact_name && (
              <Descriptions.Item label="Contact" span={2}>{f.delivery_contact_name}{f.delivery_contact_phone ? ` · ${f.delivery_contact_phone}` : ""}</Descriptions.Item>
            )}
            {f.delivery_address && (
              <Descriptions.Item label="Address" span={2}>{f.delivery_address}{f.delivery_area ? `, ${f.delivery_area}` : ""}</Descriptions.Item>
            )}
            {f.purchased_at && (
              <Descriptions.Item label="Purchased" span={2}>{fmtDate(f.purchased_at)}</Descriptions.Item>
            )}
            {f.received_at_office_at && (
              <Descriptions.Item label="At Office" span={2}>{fmtDate(f.received_at_office_at)}</Descriptions.Item>
            )}
            {f.out_for_delivery_at && (
              <Descriptions.Item label="Out for Delivery" span={2}>{fmtDate(f.out_for_delivery_at)}</Descriptions.Item>
            )}
            {f.delivered_at && (
              <Descriptions.Item label="Delivered" span={2}>{fmtDate(f.delivered_at)}</Descriptions.Item>
            )}
            {f.delivery_notes && (
              <Descriptions.Item label="Notes" span={2}>{f.delivery_notes}</Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      ))}
    </div>
  );
}

// ── Mark Payment Received Modal ───────────────────────────────────────────────
const PAYMENT_METHOD_OPTIONS = [
  { value: "cash",          label: "Cash" },
  { value: "card",          label: "Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "link",          label: "Link Payment" },
];

const CANCELLABLE_LINK_STATUSES = new Set(["pending"]);

function MarkPaymentReceivedModal({ open, payment, userId, onClose, onSuccess }) {
  const { t } = useQv4Theme();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [parkTask, setParkTask] = useState(false);
  const [fileList, setFileList] = useState([]);
  const headers = { accessToken: localStorage.getItem("accessToken") };

  const isPayLink = payment?.payment_method === "pay_link";

  const handleClose = () => {
    setFileList([]);
    setParkTask(false);
    onClose();
  };

  const handleOk = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const proofUrls = await Promise.all(
        fileList.map(async f => {
          const fd = new FormData();
          fd.append("file", f.originFileObj);
          const res = await axios.post(`${API_DOMAIN}/whatsapp/image_upload`, fd, { headers });
          return res.data;
        })
      );

      const res = await axios.post(
        `${API_DOMAIN}/payments/${payment.id}/mark-received`,
        {
          userId,
          paid_at: values.paid_at ? values.paid_at.utc().format("YYYY-MM-DD HH:mm:ss") : null,
          ...(!isPayLink && { payment_method: values.payment_method || null }),
          payment_reference: values.payment_reference?.trim() || null,
          remarks: values.remarks?.trim() || null,
          proof_images: proofUrls,
          ...(!isPayLink && parkTask && values.park_until ? {
            park_until: values.park_until.utc().format("YYYY-MM-DD HH:mm:ss"),
            park_reason: values.park_reason?.trim() || null,
          } : {}),
        },
        { headers }
      );
      if (!res.data?.success) throw new Error(res.data?.message);
      message.success("Payment marked as received");
      onSuccess();
      handleClose();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to mark payment");
    } finally { setSaving(false); }
  };

  return (
    <Modal
      open={open}
      title={`Mark Payment Received — ${payment ? fmtAED(payment.amount) : ""}`}
      onCancel={handleClose}
      onOk={handleOk}
      confirmLoading={saving}
      okText="Mark Received"
      destroyOnHidden
      styles={{ body: { overflowY: "auto", maxHeight: "calc(90vh - 160px)", paddingRight: 4 } }}
      afterOpenChange={isOpen => {
        if (isOpen) { form.setFieldsValue({ paid_at: dayjs() }); setParkTask(false); setFileList([]); }
      }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="paid_at" label="Payment Date & Time" rules={[{ required: true, message: "Required" }]}>
          <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY HH:mm" />
        </Form.Item>
        {!isPayLink && (
          <Form.Item name="payment_method" label="Payment Method">
            <Select placeholder="Select method" options={PAYMENT_METHOD_OPTIONS} allowClear />
          </Form.Item>
        )}
        <Form.Item name="payment_reference" label={isPayLink ? "Provider Transaction Reference" : "Payment Reference"}>
          <Input placeholder={isPayLink ? "e.g. TRN or provider transaction ID" : "e.g. TRN-20260628-001"} />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} placeholder="e.g. Confirmed by Accounts" />
        </Form.Item>
        <Form.Item label="Proof Images (optional)">
          <Upload
            listType="picture-card"
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: fl }) => setFileList(fl)}
            accept="image/*"
            multiple
          >
            {fileList.length < 5 && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 4, fontSize: 11 }}>Add</div>
              </div>
            )}
          </Upload>
        </Form.Item>
        {!isPayLink && (
          <>
            <Form.Item label="Park task for follow-up?">
              <input type="checkbox" checked={parkTask} onChange={e => setParkTask(e.target.checked)} style={{ marginRight: 8 }} />
              <span style={{ fontSize: 12, color: t.textSub }}>Partial payment — park until customer returns</span>
            </Form.Item>
            {parkTask && (
              <>
                <Form.Item name="park_until" label="Follow-up Date & Time" rules={[{ required: true, message: "Required" }]}>
                  <DatePicker showTime style={{ width: "100%" }} format="DD MMM YYYY HH:mm" />
                </Form.Item>
                <Form.Item name="park_reason" label="Park Reason">
                  <Input.TextArea rows={2} placeholder="e.g. Customer will pay balance tomorrow" />
                </Form.Item>
              </>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
}

// ── Payments Tab ──────────────────────────────────────────────────────────────
function PaymentsTab({ payments, afterSales, parts, canMarkReceived, onMarkReceived, canRecord, onRecord, userId, onCancelled }) {
  const { t, isDark } = useQv4Theme();
  const hasAfterSales = !!afterSales;
  // No after_sales row yet (no payment activity at all) — approximate the
  // invoice from the selected quotes' selling price so the summary isn't
  // just blank; nothing's been collected against it yet.
  const partsTotal = (parts ?? [])
    .filter(p => p.selected_quote_id && p.selling_price != null)
    .reduce((s, p) => s + Number(p.selling_price), 0);
  const saleAmount    = hasAfterSales ? Number(afterSales?.sale_amount  || 0) : partsTotal;
  const paidAmount    = hasAfterSales ? Number(afterSales?.paid_amount  || 0) : 0;
  const balanceAmount = hasAfterSales ? Number(afterSales?.balance_amount || 0) : partsTotal;
  const payStatus     = afterSales?.payment_status;

  const statusColor = payStatus === "paid" ? "#389e0d"
    : payStatus === "partial" ? "#d46b08"
    : "#cf1322";

  const [cancelTarget, setCancelTarget] = useState(null); // payment row | null
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const headers = { accessToken: localStorage.getItem("accessToken") };

  const doCancelLink = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const res = await axios.post(
        `${API_DOMAIN}/payments/${cancelTarget.id}/cancel`,
        { userId, reason: cancelReason.trim() || null },
        { headers }
      );
      if (!res.data.success) throw new Error(res.data.message);
      message.success("Payment link cancelled");
      setCancelTarget(null);
      setCancelReason("");
      onCancelled?.();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to cancel payment link");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Summary bar */}
      <div style={{ background: t.headerBg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "10px 14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: t.text }}>Payment Summary</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {payStatus && (
              <Tag color={payStatus === "paid" ? "green" : payStatus === "partial" ? "orange" : "red"} style={{ margin: 0, fontSize: 10 }}>
                {afterSales?.payment_status_label || payStatus}
              </Tag>
            )}
            {canRecord && (
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={onRecord}>
                Record Payment
              </Button>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 0, borderRadius: 6, overflow: "hidden", border: `1px solid ${t.border}` }}>
            <div style={{ flex: 1, textAlign: "center", padding: "6px 0", background: t.cardBg }}>
              <div style={{ fontSize: 10, color: t.textSub, marginBottom: 2 }}>Invoice{!hasAfterSales ? " (est.)" : ""}</div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{fmtAED(saleAmount)}</div>
            </div>
            <div style={{ width: 1, background: t.border }} />
            <div style={{ flex: 1, textAlign: "center", padding: "6px 0", background: isDark ? "rgba(82,196,26,0.12)" : "#f6ffed" }}>
              <div style={{ fontSize: 10, color: t.textSub, marginBottom: 2 }}>Collected</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#389e0d" }}>{fmtAED(paidAmount)}</div>
            </div>
            <div style={{ width: 1, background: t.border }} />
            <div style={{ flex: 1, textAlign: "center", padding: "6px 0", background: balanceAmount > 0 ? (isDark ? "rgba(250,173,20,0.12)" : "#fff7e6") : t.cardBg }}>
              <div style={{ fontSize: 10, color: t.textSub, marginBottom: 2 }}>Balance Due</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: balanceAmount > 0 ? statusColor : "#389e0d" }}>
                {fmtAED(balanceAmount)}
              </div>
            </div>
        </div>
      </div>
      {(!payments?.length) && <Empty description="No payments recorded" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "30px 0" }} />}
      {payments.map((p, i) => {
        const proofImages = Array.isArray(p.proof_images) ? p.proof_images : [];
        return (
          <Card key={p.id ?? i} size="small" style={{ borderRadius: 8 }} styles={{ body: { padding: "10px 14px" } }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: "#389e0d" }}>{fmtAED(p.amount)}</span>
                {p.payment_stage && (
                  <Tag color={
                    p.payment_stage === "advance" ? "blue" :
                    p.payment_stage === "balance" ? "purple" :
                    p.payment_stage === "full_payment" ? "green" :
                    p.payment_stage === "refund" ? "red" : "default"
                  } style={{ margin: 0, fontSize: 10 }}>
                    {p.payment_stage_label || p.payment_stage.replace("_", " ")}
                  </Tag>
                )}
              </div>
              <Tag color={p.payment_status === "received" ? "green" : "orange"} style={{ margin: 0, fontSize: 10 }}>
                {p.payment_status_label || p.payment_status}
              </Tag>
            </div>
            <div style={{ fontSize: 11, color: t.textSub, display: "flex", flexDirection: "column", gap: 2 }}>
              {p.payment_method && <span>Method: {p.payment_method}</span>}
              {p.payment_reference && <span>Ref: {p.payment_reference}</span>}
              {p.collected_by && <span>Collected by: {p.collected_by}</span>}
              {p.collection_location && <span>Location: {p.collection_location}</span>}
              {p.paid_at && <span style={{ color: t.textSub }}>{fmtDate(p.paid_at)}</span>}
              {p.remarks && <span style={{ color: t.textSub, marginTop: 2 }}>{p.remarks}</span>}
            </div>
            {proofImages.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Image.PreviewGroup>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {proofImages.map((src, j) => {
                      const url = src.startsWith("http") ? src : `${API_DOMAIN}${src}`;
                      return (
                        <Image key={j} src={url} width={56} height={56}
                          style={{ objectFit: "cover", borderRadius: 4, cursor: "pointer", border: `1px solid ${t.border}` }}
                          preview={{ src: url }}
                        />
                      );
                    })}
                  </div>
                </Image.PreviewGroup>
              </div>
            )}
            {canMarkReceived && p.payment_status !== "received" && (
              <div style={{ marginTop: 10, borderTop: `1px solid ${t.border}`, paddingTop: 10 }}>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => onMarkReceived(p)}
                >
                  Mark as Received
                </Button>
              </div>
            )}
            {p.payment_method === "pay_link" && CANCELLABLE_LINK_STATUSES.has(p.payment_status) && (
              <div style={{ marginTop: 10, borderTop: `1px solid ${t.border}`, paddingTop: 10 }}>
                <Button
                  size="small"
                  danger
                  onClick={() => { setCancelTarget(p); setCancelReason(""); }}
                >
                  Cancel Link
                </Button>
              </div>
            )}
          </Card>
        );
      })}
      <Modal
        open={!!cancelTarget}
        title="Cancel this payment link?"
        okText="Cancel Link"
        okButtonProps={{ danger: true, loading: cancelling }}
        onOk={doCancelLink}
        onCancel={() => setCancelTarget(null)}
        width={420}
      >
        <p style={{ fontSize: 13, marginBottom: 8 }}>
          This marks the link as cancelled in our system. It does not guarantee the customer
          can no longer pay via a stale copy of the link URL at the provider's side.
        </p>
        <Input.TextArea
          rows={2}
          maxLength={300}
          showCount
          placeholder="Optional reason (e.g. customer wants to pay cash instead)"
          value={cancelReason}
          onChange={e => setCancelReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}

// ── History Tab ───────────────────────────────────────────────────────────────
function HistoryTab({ movements }) {
  const { t, isDark } = useQv4Theme();
  if (!movements?.length) return <Empty description="No movements recorded" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "40px 0" }} />;

  const sorted = [...movements].sort((a, b) => new Date(b.moved_at) - new Date(a.moved_at));

  return (
    <Timeline style={{ marginTop: 8 }}
      items={sorted.map((m, i) => {
        const nextOlder = sorted[i + 1];
        const elapsedMs = nextOlder
          ? new Date(m.moved_at).getTime() - new Date(nextOlder.moved_at).getTime()
          : null;
        return {
          key: m.id,
          color: m.direction === "backward" ? "red" : m.direction === "forward" ? "green" : "blue",
          dot: m.direction === "backward"
            ? <HistoryOutlined style={{ fontSize: 12 }} />
            : <ArrowRightOutlined style={{ fontSize: 12 }} />,
          children: (
            <div style={{ paddingBottom: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 3 }}>
                <Tag style={{ margin: 0, fontSize: 10, background: t.hover, border: "none" }}>
                  {m.from_status || "—"}
                </Tag>
                <ArrowRightOutlined style={{ fontSize: 10, color: t.textSub }} />
                <Tag style={{ margin: 0, fontSize: 10, background: m.direction === "backward" ? (isDark ? "rgba(255,77,79,0.12)" : "#fff1f0") : (isDark ? "rgba(82,196,26,0.12)" : "#f6ffed"), border: "none", color: m.direction === "backward" ? "#cf1322" : "#389e0d" }}>
                  {m.to_status || "—"}
                </Tag>
                {m.direction && (
                  <Tag color={m.direction === "backward" ? "error" : m.direction === "forward" ? "success" : "default"} style={{ margin: 0, fontSize: 10 }}>
                    {m.direction}
                  </Tag>
                )}
              </div>
              {m.moved_by_name && (
                <div style={{ fontSize: 11, color: t.textSub }}>by {m.moved_by_name}</div>
              )}
              {m.reason && (
                <div style={{ fontSize: 11, color: t.textSub, fontStyle: "italic", marginTop: 2 }}>"{m.reason}"</div>
              )}
              {m.remarks && m.remarks !== m.reason && (
                <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>{m.remarks}</div>
              )}
              <div style={{ fontSize: 10, color: t.textSub, marginTop: 3 }}>{fmtDate(m.moved_at)}</div>
              <ElapsedDivider ms={elapsedMs} />
            </div>
          ),
        };
      })}
    />
  );
}

// ── Sent Suppliers Tab ────────────────────────────────────────────────────────
function SentSuppliersTab({ attempts, parts, onSendMore, userMap }) {
  const { t, isDark } = useQv4Theme();
  if (!attempts?.length) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "40px 0" }}>
        <Empty description="Not sent to suppliers yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        <Button type="primary" icon={<SendOutlined />} onClick={onSendMore}>Send to Suppliers</Button>
      </div>
    );
  }

  // Build supplier_id → quotes map from parts
  const quoteBySupplier = {};
  parts.forEach(p => {
    (p.quotes || []).forEach(q => {
      if (!quoteBySupplier[q.supplier_id]) quoteBySupplier[q.supplier_id] = { count: 0, partNames: [] };
      quoteBySupplier[q.supplier_id].count++;
      quoteBySupplier[q.supplier_id].partNames.push(p.part_name || `Part #${p.id}`);
    });
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button size="small" icon={<PlusOutlined />} onClick={onSendMore}>Send to more suppliers</Button>
      </div>
      {attempts.slice().reverse().map(attempt => (
        <div key={attempt.attempt}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Tag color="blue" style={{ margin: 0 }}>Attempt {attempt.attempt}</Tag>
            <span style={{ fontSize: 11, color: t.textSub }}>
              {attempt.sent_at ? dayjs(attempt.sent_at).format("DD MMM YYYY, hh:mm A") : "—"}
            </span>
            {attempt.sent_by && userMap?.[attempt.sent_by] && (
              <span style={{ fontSize: 11, color: t.textSub, fontWeight: 600 }}>
                · {userMap[attempt.sent_by]}
              </span>
            )}
            <div style={{ flex: 1 }} />
            <Tag style={{ margin: 0 }}>{attempt.suppliers?.length ?? 0} supplier{(attempt.suppliers?.length ?? 0) !== 1 ? "s" : ""}</Tag>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {(attempt.suppliers ?? []).map(s => {
              const q = quoteBySupplier[s.supplier_id];
              return (
                <div key={s.supplier_id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 10px", borderRadius: 6,
                  background: q ? (isDark ? "rgba(82,196,26,0.12)" : "#f6ffed") : t.headerBg,
                  border: `1px solid ${q ? "#b7eb8f" : t.border}`,
                }}>
                  <Avatar size={26} style={{ backgroundColor: getAgentColor(s.supplier_name), fontSize: 10, flexShrink: 0 }}>
                    {(s.supplier_name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.supplier_name}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
                      {s.supplier_number && (
                        <span style={{ fontSize: 10, color: t.textSub }}>{s.supplier_number}</span>
                      )}
                      {s.supplier_score != null && (
                        <Rate disabled value={s.supplier_score} count={5} style={{ fontSize: 9 }} />
                      )}
                    </div>
                  </div>
                  {q
                    ? <Tag color="green" style={{ margin: 0, fontSize: 10, flexShrink: 0 }}>{q.count} quote{q.count !== 1 ? "s" : ""} received</Tag>
                    : <Tag color="orange" style={{ margin: 0, fontSize: 10, flexShrink: 0 }}>Awaiting</Tag>
                  }
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Drawer ───────────────────────────────────────────────────────────────
export function WorkflowTaskDrawer({ queryPk, onClose, onSent, onReassignClick, onUnparkClick, onMoveBackClick, onCancelClick, onBillingDetailsSent }) {
  const { authState } = useContext(AuthContext);
  const { socket: mainSocket } = useSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [markPaymentTarget, setMarkPaymentTarget] = useState(null);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [unavailableModalOpen, setUnavailableModalOpen] = useState(false);
  const [billingDetailsModalOpen, setBillingDetailsModalOpen] = useState(false);
  const [priceDrawerOpen, setPriceDrawerOpen] = useState(false);
  const [userMap, setUserMap] = useState({});

  const queryPkRef = useRef(queryPk);
  useEffect(() => { queryPkRef.current = queryPk; }, [queryPk]);

  // Re-render once the condition code→label map arrives so parts show labels
  const [, setCondReady] = useState(false);
  useEffect(() => { loadConditionLabels().then(() => setCondReady(true)); }, []);
  useEffect(() => { loadSideLabels().then(() => setCondReady(v => !v)); }, []);
  const [sideOptions, setSideOptions] = useState([]);
  useEffect(() => { fetchMiscOptions("part_side").then(setSideOptions); }, []);
  const [conditionOptions, setConditionOptions] = useState([]);
  useEffect(() => { fetchMiscOptions("part_condition_origin").then(setConditionOptions); }, []);
  // Part-name autocomplete for the richer Add Part form (PartsEditor) — same
  // source CustomerEditDrawer's Create Query form already uses.
  const [partsInfoOptions, setPartsInfoOptions] = useState([]);
  const [partsInfoLoading, setPartsInfoLoading] = useState(false);
  useEffect(() => {
    setPartsInfoLoading(true);
    axios.post(`${API_DOMAIN}/whatsapp/get_parts_info`, {}, {
      headers: { accessToken: localStorage.getItem("accessToken") },
    }).then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setPartsInfoOptions(list.map(p =>
        typeof p === "string" ? { value: p, label: p } : { value: p.value ?? p.name, label: p.label ?? p.name ?? p.value }
      ));
    }).catch(() => {}).finally(() => setPartsInfoLoading(false));
  }, []);

  useEffect(() => {
    _fetchSRMUsers().then(list => {
      const map = {};
      list.forEach(u => { map[u.id] = u.fullName; });
      setUserMap(map);
    });
  }, []);

  const fetchData = (pk) => {
    if (!pk) return Promise.resolve(null);
    return axios
      .get(`${API_DOMAIN}/query_workflow/task/get?user_id=${authState.id}&query_pk=${pk}`, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      })
      .then(res => {
        if (res.data.success) { setData(res.data.data); return res.data.data; }
        return null;
      })
      .catch(() => null);
  };

  // A customer needs "real/legal name" + at least one address on file before we
  // can raise an SO/PO/invoice or book a Shipday shipment — check this the
  // moment a payment completes (full payment recorded, or a pending one marked
  // received) and nudge staff to request it if either is missing.
  const checkBillingDetailsNeeded = (freshData) => {
    const c = freshData?.task?.customer;
    const isPaid = freshData?.after_sales?.payment_status === "paid";
    if (!isPaid || !c) return;
    const hasRealName = !!String(c.customer_real_name || "").trim();
    const hasAddress = Array.isArray(c.customer_addresses_new) && c.customer_addresses_new.length > 0;
    if (!hasRealName || !hasAddress) setBillingDetailsModalOpen(true);
  };

  useEffect(() => {
    if (!queryPk) { setData(null); return; }
    setLoading(true);
    setData(null);
    axios
      .get(`${API_DOMAIN}/query_workflow/task/get?user_id=${authState.id}&query_pk=${queryPk}`, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      })
      .then(res => { if (res.data.success) setData(res.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [queryPk]); // eslint-disable-line

  // Silently refresh when quotes, parts, or task-level updates arrive
  useEffect(() => {
    if (!mainSocket) return;
    const handle = (data) => {
      const pk = data?.query_pk ?? data?.queryPk ?? data?.data?.task_id ?? data?.data?.query_pk ?? data?.data?.payment?.query_pk;
      if (pk && pk === queryPkRef.current) fetchData(pk);
    };
    mainSocket.on("query:quotes_updated", handle);
    mainSocket.on("query:parts_updated", handle);
    mainSocket.on("query:updated", handle);
    mainSocket.on("query:payment_manually_received", handle);
    mainSocket.on("query:payment_updated", handle);
    mainSocket.on("query:after_sales_updated", handle);
    return () => {
      mainSocket.off("query:quotes_updated", handle);
      mainSocket.off("query:parts_updated", handle);
      mainSocket.off("query:updated", handle);
      mainSocket.off("query:payment_manually_received", handle);
      mainSocket.off("query:payment_updated", handle);
      mainSocket.off("query:after_sales_updated", handle);
    };
  }, [mainSocket]); // eslint-disable-line

  const task         = data?.task         ?? null;
  const parts        = data?.parts        ?? [];
  // Parts no supplier ever quoted — the ones the customer needs to be told about.
  const zeroQuoteParts = parts.filter(p => (p.supplier_quotes ?? p.quotes ?? []).length === 0);
  const afterSales   = data?.after_sales  ?? null;
  const fulfillments = data?.fulfillments ?? [];
  const payments     = data?.payments     ?? [];
  const movements    = data?.movements    ?? [];
  const urgCfg       = task?.urgency ? URGENCY_CFG[task.urgency] : null;

  // Derive send-history data for the Sent tab and modal pre-population
  const sendAttempts = Array.isArray(task?.supplier_send_info) ? task.supplier_send_info : [];
  const initialGroups = [...new Set(sendAttempts.flatMap(a => a.selected_groups ?? []))];
  const previouslyContactedIds = new Set(sendAttempts.flatMap(a => (a.suppliers ?? []).map(s => s.supplier_id)));

  const tabs = [
    {
      key: "overview",
      label: "Overview",
      icon: <UserOutlined />,
      children: task ? <OverviewTab task={task} afterSales={afterSales} parts={parts} userMap={userMap} /> : null,
    },
    {
      key: "sent",
      label: (
        <Badge count={sendAttempts.length} size="small" offset={[6, 0]} color="#fa8c16">
          <span style={{ paddingRight: 6 }}>Sent</span>
        </Badge>
      ),
      icon: <SendOutlined />,
      children: <SentSuppliersTab attempts={sendAttempts} parts={parts} userMap={userMap} onSendMore={() => setSendModalOpen(true)} />,
    },
    {
      key: "parts",
      label: (
        <Badge count={parts.filter(p => p.status !== "disabled").length} size="small" offset={[6, 0]} color="#1677ff">
          <span style={{ paddingRight: 6 }}>Parts</span>
        </Badge>
      ),
      icon: <ShoppingOutlined />,
      children: <PartsTab
        parts={parts} images={task?.images || []} queryPk={queryPk} userId={authState?.id}
        customerPhone={task?.customer?.customer_mobile}
        onPartPatched={() => fetchData(queryPk)} onManagePrices={() => setPriceDrawerOpen(true)}
        sideOptions={sideOptions} conditionOptions={conditionOptions}
        partsInfoOptions={partsInfoOptions} partsInfoLoading={partsInfoLoading}
        onAddNewPartInfo={async (value) => {
          // Just the network call + shared cache update — PartsTab's own
          // wrapper applies the result to whichever staged part triggered it.
          await axios.post(`${API_DOMAIN}/whatsapp/add_new_part_info`, { value }, {
            headers: { accessToken: localStorage.getItem("accessToken") },
          });
          setPartsInfoOptions(prev => [...prev, { value, label: value }]);
        }}
      />,
    },
    {
      key: "fulfillment",
      label: (
        <Badge count={fulfillments.length} size="small" offset={[6, 0]} color="#722ed1">
          <span style={{ paddingRight: 6 }}>Fulfillment</span>
        </Badge>
      ),
      icon: <TruckOutlined />,
      children: <FulfillmentTab fulfillments={fulfillments} parts={parts} />,
    },
    {
      key: "payments",
      label: (
        <Badge count={payments.length} size="small" offset={[6, 0]} color="#52c41a">
          <span style={{ paddingRight: 6 }}>Payments</span>
        </Badge>
      ),
      icon: <DollarOutlined />,
      children: <PaymentsTab
        payments={payments}
        afterSales={afterSales}
        parts={parts}
        canMarkReceived={!!(authState?.user?.admin || (task?.assigned_user?.id && task.assigned_user.id === authState?.id))}
        onMarkReceived={p => setMarkPaymentTarget(p)}
        canRecord={!!(authState?.user?.admin || (task?.assigned_user?.id && task.assigned_user.id === authState?.id))}
        onRecord={() => setRecordPaymentOpen(true)}
        userId={authState?.id}
        onCancelled={() => fetchData(queryPk)}
      />,
    },
    {
      key: "history",
      label: (
        <Badge count={movements.length} size="small" offset={[6, 0]} color="#8c8c8c">
          <span style={{ paddingRight: 6 }}>History</span>
        </Badge>
      ),
      icon: <HistoryOutlined />,
      children: <HistoryTab movements={movements} />,
    },
  ];

  return (
    <>
    <Drawer
      open={!!queryPk}
      onClose={onClose}
      width={720}
      push={false}
      destroyOnHidden
      title={
        task ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700 }}>#{task.query_id}</span>
            <Tag color="blue" style={{ margin: 0 }}>{task.status_name || task.status}</Tag>
            {urgCfg && (
              <Tag style={{ margin: 0, color: urgCfg.color, borderColor: urgCfg.color, background: urgCfg.pill }}>
                {urgCfg.label}
              </Tag>
            )}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              {onUnparkClick && ["parked", "on_hold", "postponed"].includes(task?.status) && (
                <Button size="small" icon={<RollbackOutlined />} onClick={() => onUnparkClick(queryPk)}>
                  Unpark
                </Button>
              )}
              {onReassignClick && (
                <Button size="small" icon={<SwapOutlined />} onClick={() => onReassignClick(queryPk)}>
                  Reassign
                </Button>
              )}
              {onMoveBackClick && movableBackStatuses(task?.status).length > 0 && (
                <Button size="small" icon={<RollbackOutlined />} onClick={() => onMoveBackClick(queryPk, task?.query_id, task?.status)}>
                  Move Back
                </Button>
              )}
              {task?.status !== "draft" && zeroQuoteParts.length > 0 && (
                <Button size="small" icon={<NotificationOutlined />} onClick={() => setUnavailableModalOpen(true)}>
                  Notify Unavailable Parts
                </Button>
              )}
              {onCancelClick && !["cancelled", "completed", "disabled"].includes(task?.status) && (
                <Button size="small" danger icon={<StopOutlined />} onClick={() => onCancelClick(queryPk, task?.query_id)}>
                  Mark as Lost
                </Button>
              )}
            </div>
          </div>
        ) : "Task"
      }
      styles={{ body: { padding: "0 20px 20px", overflowY: "auto" } }}
    >
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && !task && queryPk && (
        <Empty description="Task not found" style={{ padding: 60 }} />
      )}

      {!loading && task && (
        <Tabs
          defaultActiveKey="overview"
          size="small"
          style={{ marginTop: 4 }}
          items={tabs}
        />
      )}
    </Drawer>

    <SendToSuppliersModal
      queryPk={queryPk}
      task={task}
      open={sendModalOpen}
      userId={authState?.id}
      initialGroups={initialGroups}
      previouslyContactedIds={previouslyContactedIds}
      onClose={() => setSendModalOpen(false)}
      onSent={(supplierIds) => { onSent?.(queryPk, supplierIds); }}
    />

    {markPaymentTarget && (
      <MarkPaymentReceivedModal
        open={!!markPaymentTarget}
        payment={markPaymentTarget}
        userId={authState?.id}
        onClose={() => setMarkPaymentTarget(null)}
        onSuccess={() => fetchData(queryPk).then(checkBillingDetailsNeeded)}
      />
    )}

    <PriceUpdateDrawer
      card={task ? { id: queryPk, query_id: task.query_id, status: task.status, customer: task.customer, vehicle: task.vehicle } : null}
      open={priceDrawerOpen}
      onClose={() => { setPriceDrawerOpen(false); fetchData(queryPk); }}
      userId={authState?.id}
      authState={authState}
      userMap={userMap}
    />

    <RecordPaymentModal
      open={recordPaymentOpen}
      queryPk={queryPk}
      userId={authState?.id}
      afterSales={afterSales}
      parts={parts}
      onClose={() => setRecordPaymentOpen(false)}
      onSuccess={() => { fetchData(queryPk).then(checkBillingDetailsNeeded); }}
    />

    <UnavailablePartsModal
      open={unavailableModalOpen}
      queryPk={queryPk}
      queryId={task?.query_id}
      customerName={task?.customer?.customer_name}
      customerPhone={task?.customer?.customer_mobile}
      unavailableParts={zeroQuoteParts}
      userId={authState?.id}
      onClose={() => setUnavailableModalOpen(false)}
      onSuccess={() => { fetchData(queryPk); }}
    />

    <BillingDetailsModal
      open={billingDetailsModalOpen}
      queryPk={queryPk}
      queryId={task?.query_id}
      customerName={task?.customer?.customer_real_name || task?.customer?.customer_name}
      customerPhone={task?.customer?.customer_mobile}
      userId={authState?.id}
      onClose={() => setBillingDetailsModalOpen(false)}
      onSuccess={() => {
        setBillingDetailsModalOpen(false);
        onBillingDetailsSent?.(task?.customer?.customer_mobile);
      }}
    />
  </>
  );
}
